import { Box, Link as MuiLink, Stack, Tooltip, Typography } from "@mui/material";
import { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { StatusChip } from "components/StatusChip";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { StaffCersRejectionFindingsQuery } from "generated-graphql/graphql";
import { GridSelectionProvider, OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { GET_STAFF_CERS_REJECTION_FINDINGS } from "hooks/useStaffCersRejectionFindings";
import { useCallback, useMemo } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

import { BulkCreateRevisions } from "./BulkCreateRevisions";
import { IgnoredFindingsToggle } from "./IgnoredFindingsToggle";
import { MyFindingsToggle } from "./MyFindingsToggle";
import { RejectionActionDrawer } from "./RejectionActionDrawer";
import { RejectionStatCards } from "./RejectionStatCards";
import {
  StaffCersRejectionFindingRow,
  findingAgeDays,
  isFindingSelectable,
  resolutionStatusColor,
} from "./rejectionStatus";

// ── Stable, module-level config (V2 requires stable prop references) ──
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const INITIAL_SORT_MODEL: GridSortModel = [{ field: "decisionDate", sort: "desc" }];
const FILTER_GROUPS: FilterGroup[] = [
  { name: "Facility", fields: ["facilityName"] },
  { name: "Tenant", fields: ["tenantName"] },
  { name: "CUPA", fields: ["cupaName"] },
  { name: "CSM", fields: ["csmName"] },
  { name: "Status", fields: ["status"] },
  { name: "Module", fields: ["module"] },
];

const getItems = (data: StaffCersRejectionFindingsQuery) =>
  data.staffCersRejectionFindings.items ?? [];
const getCount = (data: StaffCersRejectionFindingsQuery) =>
  data.staffCersRejectionFindings.count ?? 0;

function formatDate(value: string | null | undefined): string {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export const Rejections = () => {
  useBreadcrumb({ label: "Rejections", to: "/staff/cers/rejections" });

  // Drawer open-state lives in the URL so notifications/digests can deep-link
  // straight to a finding (?findingId=). Coexists with omnisearch's own params.
  const [searchParams, setSearchParams] = useSearchParams();
  const findingId = searchParams.get("findingId");

  const openFinding = useCallback(
    (params: GridRowParams<StaffCersRejectionFindingRow>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("findingId", String(params.id));
        return next;
      });
    },
    [setSearchParams]
  );

  const closeFinding = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("findingId");
      return next;
    });
  }, [setSearchParams]);

  const columns: OmnisearchGridColDef<StaffCersRejectionFindingRow>[] = useMemo(
    () => [
      {
        field: "facilityName",
        headerName: "Facility",
        flex: 0.8,
        renderCell: ({ row }) => {
          if (row.encampFacilityId && row.encampTenantId) {
            return (
              <MuiLink
                component={RouterLink}
                to={`/o/${row.encampTenantId}/facilities/${row.encampFacilityId}`}
                onClick={(e) => e.stopPropagation()}
              >
                {row.encampFacilityName ?? row.facilityName}
              </MuiLink>
            );
          }
          return <>{row.facilityName}</>;
        },
      },
      { field: "tenantName", headerName: "Tenant", flex: 0.6 },
      { field: "csmName", headerName: "CSM", flex: 0.5 },
      { field: "cupaName", headerName: "CUPA", flex: 0.6 },
      {
        field: "module",
        headerName: "Module",
        flex: 0.5,
        valueGetter: (_, row) => prettyPrintEnumValue(row.module),
      },
      {
        field: "decisionDate",
        headerName: "Decision Date",
        flex: 0.45,
        valueGetter: (_, row) => formatDate(row.decisionDate),
      },
      {
        field: "age",
        headerName: "Age (days)",
        flex: 0.35,
        sortable: false,
        valueGetter: (_, row) => findingAgeDays(row),
      },
      {
        field: "issueCategories",
        headerName: "Issue Categories",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => {
          if (!row.issueCategories?.length) return null;
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
              {row.issueCategories.map((category) => (
                <StatusChip key={category} label={prettyPrintEnumValue(category)} color="default" />
              ))}
            </Box>
          );
        },
      },
      {
        field: "llmSummary",
        headerName: "Summary",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => {
          if (!row.llmSummary) return null;
          return (
            <Tooltip title={row.llmSummary}>
              <Typography variant="body2" noWrap>
                {row.llmSummary}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "revisionReportId",
        headerName: "Linked Submission",
        flex: 0.5,
        sortable: false,
        renderCell: ({ row }) => {
          if (!row.revisionReportId) return null;
          return (
            <MuiLink
              component={RouterLink}
              to={`/staff/fulfillment/r/${row.revisionReportId}`}
              onClick={(e) => e.stopPropagation()}
            >
              View report
            </MuiLink>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.45,
        renderCell: ({ row }) => (
          <StatusChip
            label={prettyPrintEnumValue(row.status)}
            color={resolutionStatusColor(row.status)}
          />
        ),
      },
    ],
    []
  );

  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => ({
      search: omnisearch || undefined,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
    }),
    []
  );

  const isRowSelectable = useCallback(
    (params: GridRowParams<StaffCersRejectionFindingRow>) => isFindingSelectable(params.row),
    []
  );

  // Personal scope filter lives in the grid command bar (right-section toolbar),
  // matching the Customer/Violations "Assigned to me" placement. MyFindingsToggle
  // renders null for non-CSMs, so it simply doesn't appear for them.
  const commandButtons = useMemo(
    () => [
      <MyFindingsToggle key="my-findings" />,
      <IgnoredFindingsToggle key="ignored-findings" />,
      <BulkCreateRevisions key="bulk-create-revisions" />,
    ],
    []
  );

  return (
    <GridSelectionProvider>
      <Stack sx={{ pt: 3 }} spacing={2}>
        <RejectionStatCards />
        <OmnisearchDataGridV2
          query={GET_STAFF_CERS_REJECTION_FINDINGS}
          getItems={getItems}
          getCount={getCount}
          persistenceKey="staff-cers-rejections-v2"
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          initialSortModel={INITIAL_SORT_MODEL}
          filterGroups={FILTER_GROUPS}
          columns={columns}
          commandButtons={commandButtons}
          buildVariables={buildVariables}
          noRowsMessage="No CERS rejection findings found."
          checkboxSelection
          disableRowSelectionOnClick
          isRowSelectable={isRowSelectable}
          onRowClick={openFinding}
        />
        {/* Checkboxes select for bulk actions (disableRowSelectionOnClick);
            clicking the row body retargets the non-modal drawer. */}
        <RejectionActionDrawer findingId={findingId} onClose={closeFinding} />
      </Stack>
    </GridSelectionProvider>
  );
};
